<template>
  <div class="task-chat-container">
    <!-- Task Details Header -->
    <b-card class="mb-2 task-details-card" v-if="taskDetails">
      <div class="task-header-row">
        <feather-icon icon="FileTextIcon" size="18" class="mr-2 text-primary" />
        <strong class="task-title">{{ taskDetails.subject || 'N/A' }}</strong>
      </div>
      <b-row class="task-details-row">
        <b-col md="6" class="task-details-col">
          <div class="task-detail-item">
            <strong class="text-muted small">Priority:</strong>
            <b-badge :variant="getPriorityVariant(taskDetails.priority)" class="ml-2">
              {{ taskDetails.priority || 'N/A' }}
            </b-badge>
          </div>
          <div class="task-detail-item">
            <strong class="text-muted small">Status:</strong>
            <b-badge :variant="getStatusVariant(taskDetails.status)" class="ml-2">
              {{ taskDetails.status || 'N/A' }}
            </b-badge>
          </div>
          <div class="task-detail-item" v-if="taskDetails.latest_status">
            <strong class="text-muted small">Latest Status:</strong>
            <b-badge :variant="getStatusValueVariant(taskDetails.latest_status)" class="ml-2">
              {{ taskDetails.latest_status }}
            </b-badge>
          </div>
        </b-col>
        <b-col md="6" class="task-details-col">
          <div class="task-detail-item" v-if="taskDetails.enterby">
            <strong class="text-muted small">Created By:</strong>
            <span class="ml-2">{{ getCreatedByName(taskDetails.enterby) }}</span>
          </div>
          <div class="task-detail-item" v-if="taskDetails.task_date">
            <strong class="text-muted small">Created Date:</strong>
            <span class="ml-2">{{ formatDate(taskDetails.task_date) }}</span>
          </div>
          <div class="task-detail-item" v-if="taskDetails.deadline_date">
            <strong class="text-muted small">Deadline:</strong>
            <span class="ml-2">{{ formatDate(taskDetails.deadline_date) }}</span>
          </div>
        </b-col>
      </b-row>
      <div v-if="taskDetails.description" class="task-description-section">
        <strong class="text-muted small d-block mb-1">Description:</strong>
        <p class="mb-0 small">{{ taskDetails.description }}</p>
      </div>
      <!-- Task Attachments -->
      <div v-if="taskDetails.attachment1 || taskDetails.attachment2 || taskDetails.voice_note" class="task-attachments-section">
        <strong class="text-muted small d-block mb-1">Attachments:</strong>
        <div class="attachments-list">
          <span v-if="taskDetails.attachment1" class="attachment-item-inline mr-3">
            <feather-icon icon="PaperclipIcon" size="14" class="mr-1 text-primary" />
            <b-link :href="getTaskAttachmentUrl(taskDetails.attachment1)" target="_blank" class="small">
              {{ getFileName(taskDetails.attachment1) }}
            </b-link>
          </span>
          <span v-if="taskDetails.attachment2" class="attachment-item-inline mr-3">
            <feather-icon icon="PaperclipIcon" size="14" class="mr-1 text-primary" />
            <b-link :href="getTaskAttachmentUrl(taskDetails.attachment2)" target="_blank" class="small">
              {{ getFileName(taskDetails.attachment2) }}
            </b-link>
          </span>
          <span v-if="taskDetails.voice_note" class="attachment-item-inline">
            <feather-icon icon="Volume2Icon" size="14" class="mr-1 text-success" />
            <audio :src="getTaskVoiceNoteUrl(taskDetails.voice_note)" controls class="voice-player-inline" />
          </span>
        </div>
      </div>
    </b-card>

    <!-- Chat Messages Area -->
    <b-card class="chat-messages-card">
      <div class="chat-messages" ref="chatMessages" v-if="filteredChatHistory.length > 0">
        <div
          v-for="(message, index) in filteredChatHistory"
          :key="index"
          :class="['message-item', getMessageClass(message)]"
        >
          <!-- Chat Message Item (skip task info item) -->
          <div v-if="message.id" class="chat-message">
            <div class="message-header">
              <b-avatar
                :src="getProfileImage((message.from_user && message.from_user.id) || message.from_user_id)"
                size="32"
                class="mr-2"
              >
                <feather-icon icon="UserIcon" size="16" v-if="!getProfileImage((message.from_user && message.from_user.id) || message.from_user_id)" />
              </b-avatar>
              <div class="message-info">
                <strong>{{ getMessageSenderName(message) }}</strong>
                <span class="text-muted small ml-2">
                  {{ formatDateTime(message.created_at || message.enterdatetime) }}
                </span>
              </div>
            </div>
            
            <!-- Status Update Badge -->
            <div v-if="message.status_value" class="status-update-badge mb-2">
              <b-badge :variant="getStatusValueVariant(message.status_value)">
                Status Updated: {{ message.status_value }}
              </b-badge>
            </div>

            <!-- Message Text -->
            <div v-if="message.message_text" class="message-text">
              {{ message.message_text }}
            </div>

            <!-- Attachment -->
            <div v-if="message.attachment_url || message.attachment_path" class="message-attachment mt-2">
              <div class="attachment-container">
                <feather-icon icon="PaperclipIcon" size="18" class="mr-2 text-primary" />
                <div class="attachment-info">
                  <strong class="d-block mb-1">Attachment:</strong>
                  <b-link
                    :href="message.attachment_url || message.attachment_path"
                    target="_blank"
                    class="d-inline-flex align-items-center attachment-link"
                  >
                    <feather-icon icon="DownloadIcon" size="14" class="mr-1" />
                    <span>Download Attachment</span>
                  </b-link>
                  <div v-if="isImageFile(message.attachment_url || message.attachment_path)" class="mt-2">
                    <b-img
                      :src="message.attachment_url || message.attachment_path"
                      thumbnail
                      fluid
                      style="max-width: 300px; max-height: 200px; cursor: pointer;"
                      @click="openImageModal(message.attachment_url || message.attachment_path)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Voice Note -->
            <div v-if="message.voice_url" class="message-voice-note mt-2">
              <div class="d-flex align-items-center">
                <feather-icon icon="Volume2Icon" size="16" class="mr-2 text-success" />
                <audio
                  :ref="`audio-${index}`"
                  :src="message.voice_url"
                  controls
                  class="flex-grow-1"
                  style="max-width: 300px;"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5 text-muted">
        <feather-icon icon="MessageSquareIcon" size="48" class="mb-2" />
        <p>No messages yet. Start the conversation!</p>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="text-center py-2">
        <b-spinner small></b-spinner>
        <span class="ml-2">Loading messages...</span>
      </div>
    </b-card>

    <!-- Message Input Area -->
    <b-card class="message-input-card">
      <b-form @submit.prevent="sendMessage">
        <b-row>
          <b-col md="4">
            <b-form-group label="Status Update" label-size="sm">
              <v-select
                v-model="messageForm.status_value"
                :options="statusOptions"
                placeholder="Select status (required)"
                :clearable="false"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="8">
            <b-form-group label="Message" label-size="sm">
              <b-input-group>
                <b-form-input
                  v-model="messageForm.message_text"
                  placeholder="Type your message..."
                  required
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="triggerFileInput"
                    :disabled="sending"
                    title="Attach File"
                  >
                    <feather-icon icon="PaperclipIcon" size="16" />
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    @click="toggleVoiceRecording"
                    :disabled="sending"
                    :variant="isRecording ? 'danger' : 'outline-secondary'"
                    title="Record Voice Note"
                  >
                    <feather-icon
                      :icon="isRecording ? 'SquareIcon' : 'MicIcon'"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    type="submit"
                    variant="primary"
                    :disabled="sending || !messageForm.message_text"
                  >
                    <feather-icon icon="SendIcon" size="16" class="mr-1" />
                    {{ sending ? 'Sending...' : 'Send' }}
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Recording Indicator -->
        <div v-if="isRecording" class="recording-indicator mt-2">
          <b-alert variant="danger" show class="mb-0 py-2">
            <feather-icon icon="RadioIcon" size="14" class="mr-2" />
            Recording... {{ formatTime(recordingTime) }}
            <b-button
              variant="outline-danger"
              size="sm"
              class="ml-2"
              @click="stopRecording"
            >
              Stop
            </b-button>
          </b-alert>
        </div>

        <!-- Attachment Preview -->
        <div v-if="messageForm.attachment_path" class="attachment-preview mt-2">
          <b-badge variant="info" class="d-inline-flex align-items-center">
            <feather-icon icon="PaperclipIcon" size="14" class="mr-1" />
            Attachment ready
            <feather-icon
              icon="XIcon"
              size="14"
              class="ml-2 cursor-pointer"
              @click="removeAttachment"
            />
          </b-badge>
        </div>

        <!-- Voice Note Preview -->
        <div v-if="messageForm.voice_path" class="voice-note-preview mt-2">
          <b-badge variant="success" class="d-inline-flex align-items-center">
            <feather-icon icon="Volume2Icon" size="14" class="mr-1" />
            Voice note ready
            <feather-icon
              icon="XIcon"
              size="14"
              class="ml-2 cursor-pointer"
              @click="removeVoiceNote"
            />
          </b-badge>
        </div>
      </b-form>

      <!-- Hidden File Input -->
      <input
        ref="fileInput"
        type="file"
        style="display: none"
        @change="handleFileSelect"
        accept="image/*,application/pdf,.doc,.docx"
      />
    </b-card>
  </div>
</template>

<script>
import axios from '@/components/axios';
import vSelect from 'vue-select';
import moment from 'moment';
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BSpinner,
  BLink,
  BAlert,
  BImg,
} from 'bootstrap-vue';

export default {
  name: 'TaskChat',
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    vSelect,
    BSpinner,
    BLink,
    BAlert,
    BImg,
  },
  props: {
    taskId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem('accessToken'),
      imagePath: process.env.VUE_APP_IMAGE_PATH,
      taskDetails: null,
      chatHistory: [],
      loading: false,
      sending: false,
      pollingInterval: null,
      statusOptions: ['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%', 'Completed'],
      messageForm: {
        status_value: '10%', // Default status value (required by API)
        message_text: '',
        to_user_id: null,
        attachment_path: null,
        voice_path: null,
      },
      participantOptions: [],
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      recordingTime: 0,
      recordingTimer: null,
      currentUserId: null,
    };
  },
  computed: {
    filteredChatHistory() {
      // Filter out the task details item - chat messages have from_user, to_user, message_text, or status_value
      // Task object has subject, assignto, cc, etc. but not chat-specific fields
      return this.chatHistory.filter(msg => {
        // If it has chat-specific fields, it's a chat message
        return msg.from_user || msg.from_user_id || msg.to_user || msg.to_user_id || msg.message_text || msg.status_value;
      });
    },
  },
  mounted() {
    const userData = JSON.parse(localStorage.getItem('userData'));
    this.currentUserId = userData && userData.id ? userData.id : null;
    this.loadChatHistory();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.isRecording) {
      this.stopRecording();
    }
  },
  methods: {
    async loadChatHistory() {
      this.loading = true;
      try {
        const response = await axios({
          method: 'GET',
          url: `${this.baseApi}/tasks/${this.taskId}/chat`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
        });

        if (response.data && response.data.data) {
          const data = response.data.data;
          this.taskDetails = data.task;
          this.chatHistory = data.chat_history || [];
          
          // Build participant options from task
          this.buildParticipantOptions();
          
          // Scroll to bottom after loading
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        this.$swal({
          title: 'Error',
          text: 'Failed to load chat history. Please try again.',
          icon: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    buildParticipantOptions() {
      const participants = new Set();
      
      // Add assigned user ID
      if (this.taskDetails && this.taskDetails.assignto) {
        const assignToId = typeof this.taskDetails.assignto === 'object' 
          ? this.taskDetails.assignto.id 
          : this.taskDetails.assignto;
        if (assignToId) participants.add(assignToId);
      }
      
      // Add CC users
      if (this.taskDetails && this.taskDetails.cc && Array.isArray(this.taskDetails.cc)) {
        this.taskDetails.cc.forEach(userId => {
          const ccId = typeof userId === 'object' ? userId.id : userId;
          if (ccId) participants.add(ccId);
        });
      }
      
      // Add creator
      if (this.taskDetails && this.taskDetails.enterby) {
        const enterById = typeof this.taskDetails.enterby === 'object' 
          ? this.taskDetails.enterby.id 
          : this.taskDetails.enterby;
        if (enterById) participants.add(enterById);
      }
      
      // Convert to array and fetch user details
      const participantIds = Array.from(participants);
      this.fetchParticipantDetails(participantIds);
    },

    async fetchParticipantDetails(userIds) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        let url = '';
        
        if (userData && userData.role === 'admin') {
          url = `${this.baseApi}/getUsers`;
        } else {
          url = `${this.baseApi}/getUserByParentId/${userData && userData.id ? userData.id : ''}`;
        }

        const response = await axios({
          method: 'GET',
          url: url,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
        });

        if (response.data && response.data.data) {
          const allUsers = Array.isArray(response.data.data) ? response.data.data : [];
          
          // Filter to only include participants
          this.participantOptions = allUsers.filter(user => {
            return userIds.includes(user.id) && user.id !== this.currentUserId;
          });

          // If using store data (for non-admin users)
          if (this.$store.state.app && this.$store.state.app.all_users) {
            const storeUsers = this.$store.state.app.all_users.filter(user => {
              return userIds.includes(user.id) && user.id !== this.currentUserId;
            });
            
            // Merge and remove duplicates
            const merged = [...this.participantOptions, ...storeUsers];
            const unique = merged.filter((user, index, self) =>
              index === self.findIndex(u => u.id === user.id)
            );
            this.participantOptions = unique;
          }

          // Set default to_user_id if only one option
          if (this.participantOptions.length === 1 && !this.messageForm.to_user_id) {
            this.messageForm.to_user_id = this.participantOptions[0];
          }
        }
      } catch (error) {
        console.error('Error fetching participant details:', error);
      }
    },

    async sendMessage() {
      // Prevent double submission
      if (this.sending) {
        return;
      }

      if (!this.messageForm.message_text) {
        return;
      }

      // Get the assigned user ID for to_user_id
      // The API will automatically send notifications to all participants (assigned user + CC users)
      let toUserId = null;
      if (this.taskDetails && this.taskDetails.assignto) {
        toUserId = typeof this.taskDetails.assignto === 'object' 
          ? this.taskDetails.assignto.id 
          : this.taskDetails.assignto;
      } else if (this.taskDetails && this.taskDetails.cc && Array.isArray(this.taskDetails.cc) && this.taskDetails.cc.length > 0) {
        // Fallback to first CC user if no assigned user
        const firstCC = this.taskDetails.cc[0];
        toUserId = typeof firstCC === 'object' ? firstCC.id : firstCC;
      }

      if (!toUserId || toUserId === this.currentUserId) {
        this.$swal({
          title: 'Error',
          text: 'No valid recipients found for this task.',
          icon: 'error',
        });
        return;
      }

      // Store message text before clearing
      const messageText = this.messageForm.message_text;
      const attachmentPath = this.messageForm.attachment_path;
      const voicePath = this.messageForm.voice_path;

      // Clear message text immediately (optimistic update)
      this.messageForm.message_text = '';
      this.messageForm.attachment_path = null;
      this.messageForm.voice_path = null;

      this.sending = true;
      try {
        // Upload attachment if present
        let uploadedAttachmentPath = attachmentPath;
        if (this.$refs.fileInput && this.$refs.fileInput.files && this.$refs.fileInput.files.length > 0 && !attachmentPath) {
          uploadedAttachmentPath = await this.uploadAttachment(this.$refs.fileInput.files[0]);
        }

        // Upload voice note if present
        let uploadedVoicePath = null;
        if (this.audioChunks.length > 0) {
          // If we have audio chunks, upload them (this is the source of truth)
          uploadedVoicePath = await this.uploadVoiceNote();
        } else if (voicePath) {
          // If voice_path exists but no audioChunks, check if it's a blob URL or already uploaded
          if (voicePath.startsWith('blob:')) {
            // If voice_path is a blob URL, convert it to a file and upload
            const response = await fetch(voicePath);
            const blob = await response.blob();
            const audioFile = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
            uploadedVoicePath = await this.uploadAttachment(audioFile);
          } else {
            // Already uploaded path, use as-is
            uploadedVoicePath = voicePath;
          }
        }

        // Send ONE message - API will handle notifications to all participants
        const messageData = {
          from_user_id: this.currentUserId,
          to_user_id: toUserId,
          status_value: this.messageForm.status_value || '10%', // Required by API, default to 10%
          message_text: messageText,
          attachment_path: uploadedAttachmentPath || null,
          voice_path: uploadedVoicePath || null,
        };

        const response = await axios({
          method: 'POST',
          url: `${this.baseApi}/tasks/${this.taskId}/chat`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
          data: JSON.stringify(messageData),
        });

        // Clear file input and audio chunks
        this.audioChunks = [];
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }

        // Reload chat history
        await this.loadChatHistory();

        if (response.data && response.data.success) {
          this.$swal({
            title: 'Success',
            text: 'Message sent successfully!',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // Restore message text if send failed
        this.messageForm.message_text = messageText;
        this.messageForm.attachment_path = attachmentPath;
        this.messageForm.voice_path = voicePath;
        this.$swal({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to send message. Please try again.',
          icon: 'error',
        });
      } finally {
        this.sending = false;
      }
    },

    async uploadAttachment(file) {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'task');

      const response = await axios({
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${this.accessToken}`,
        },
        data: formData,
        url: `${this.baseApi}/attachment`,
      });

      if (response.data && response.data.attachment) {
        return response.data.attachment;
      }
      return null;
    },

    async uploadVoiceNote() {
      if (this.audioChunks.length === 0) return null;

      const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
      const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, {
        type: 'audio/webm',
      });

      return await this.uploadAttachment(audioFile);
    },

    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.messageForm.attachment_path = await this.uploadAttachment(file);
      }
    },

    removeAttachment() {
      this.messageForm.attachment_path = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async toggleVoiceRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingTime = 0;

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          this.messageForm.voice_path = URL.createObjectURL(audioBlob);
          stream.getTracks().forEach(track => track.stop());
        };

        this.mediaRecorder.start();
        this.isRecording = true;

        this.recordingTimer = setInterval(() => {
          this.recordingTime++;
        }, 1000);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        this.$swal({
          title: 'Error',
          text: 'Could not access microphone. Please check permissions.',
          icon: 'error',
        });
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        if (this.recordingTimer) {
          clearInterval(this.recordingTimer);
          this.recordingTimer = null;
        }
      }
    },

    removeVoiceNote() {
      this.messageForm.voice_path = null;
      this.audioChunks = [];
    },

    startPolling() {
      // Poll every 5 seconds
      this.pollingInterval = setInterval(() => {
        if (!this.loading && !this.sending) {
          this.loadChatHistory();
        }
      }, 5000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages;
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    },

    getMessageClass(message) {
      if (!message.id) return 'task-info';
      
      const fromUserId = (message.from_user && message.from_user.id) || message.from_user_id;
      if (fromUserId === this.currentUserId) {
        return 'message-sent';
      }
      return 'message-received';
    },

    getMessageSenderName(message) {
      // Check if from_user object exists (from API response)
      if (message.from_user) {
        const name = message.from_user.name || message.from_user.fullname || message.from_user.username;
        if (name) return name;
      }
      
      // Try to get from store using from_user_id
      const userId = message.from_user_id || (message.from_user && message.from_user.id);
      if (userId && this.$store.state.app && this.$store.state.app.all_users) {
        const user = this.$store.state.app.all_users.find(u => u.id == userId);
        if (user) {
          return user.name || user.fullname || user.username || `User ${userId}`;
        }
      }
      
      // Fallback to enterby if available
      if (message.enterby && this.$store.state.app && this.$store.state.app.all_users) {
        const user = this.$store.state.app.all_users.find(u => u.id == message.enterby);
        if (user) {
          return user.name || user.fullname || user.username || `User ${message.enterby}`;
        }
      }
      
      // Last resort - show user ID if available
      if (userId) {
        return `User ${userId}`;
      }
      
      return 'Unknown User';
    },

    getProfileImage(userIdOrImage) {
      if (!userIdOrImage) return null;
      
      // If it's already an image path/URL
      if (typeof userIdOrImage === 'string') {
        // Check if it's a URL or path
        if (userIdOrImage.includes('http') || userIdOrImage.includes(this.imagePath)) {
          return userIdOrImage.includes(this.imagePath) 
            ? userIdOrImage 
            : this.imagePath + userIdOrImage;
        }
        // If it's a number as string, try to find user
        const numId = parseInt(userIdOrImage);
        if (!isNaN(numId) && this.$store.state.app && this.$store.state.app.all_users) {
          const user = this.$store.state.app.all_users.find(u => u.id == numId);
          if (user && user.profile_image) {
            return user.profile_image.includes(this.imagePath) 
              ? user.profile_image 
              : this.imagePath + user.profile_image;
          }
        }
      }
      
      // If it's a number (user ID), try to get from store
      if (typeof userIdOrImage === 'number' && this.$store.state.app && this.$store.state.app.all_users) {
        const user = this.$store.state.app.all_users.find(u => u.id == userIdOrImage);
        if (user && user.profile_image) {
          return user.profile_image.includes(this.imagePath) 
            ? user.profile_image 
            : this.imagePath + user.profile_image;
        }
      }
      
      return null;
    },

    getAssignedToName(assignto) {
      if (!assignto) return 'N/A';
      if (typeof assignto === 'object' && assignto.fullname) {
        return assignto.fullname || assignto.name || assignto.username || 'N/A';
      }
      if (this.$store.state.app && this.$store.state.app.all_users) {
        const user = this.$store.state.app.all_users.find(u => u.id === assignto);
        if (user) {
          return user.fullname || user.name || user.username || `User ${assignto}`;
        }
      }
      return `User ${assignto}`;
    },

    getCreatedByName(enterby) {
      if (!enterby) return 'N/A';
      if (typeof enterby === 'object' && enterby.fullname) {
        return enterby.fullname || enterby.name || enterby.username || 'N/A';
      }
      if (this.$store.state.app && this.$store.state.app.all_users) {
        const user = this.$store.state.app.all_users.find(u => u.id === enterby);
        if (user) {
          return user.fullname || user.name || user.username || `User ${enterby}`;
        }
      }
      return `User ${enterby}`;
    },

    isImageFile(url) {
      if (!url) return false;
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
      const lowerUrl = url.toLowerCase();
      return imageExtensions.some(ext => lowerUrl.includes(ext));
    },

    openImageModal(imageUrl) {
      // Open image in new window/tab
      window.open(imageUrl, '_blank');
    },

    getTaskAttachmentUrl(attachment) {
      if (!attachment) return null;
      
      // If it's already a full URL
      if (attachment.includes('http')) {
        return attachment;
      }
      
      // Otherwise, prepend image path
      return this.imagePath + attachment;
    },

    getTaskVoiceNoteUrl(voiceNote) {
      if (!voiceNote) return null;
      
      // If it's already a full URL
      if (voiceNote.includes('http')) {
        return voiceNote;
      }
      
      // Otherwise, prepend image path
      return this.imagePath + voiceNote;
    },

    getFileName(path) {
      if (!path) return '';
      
      // Extract filename from path
      if (path.includes('/')) {
        return path.split('/').pop();
      }
      
      return path;
    },

    getPriorityVariant(priority) {
      const variants = {
        High: 'danger',
        Medium: 'warning',
        Low: 'info',
      };
      return variants[priority] || 'secondary';
    },

    getStatusVariant(status) {
      const variants = {
        Open: 'primary',
        Pending: 'warning',
        Complete: 'success',
      };
      return variants[status] || 'secondary';
    },

    getStatusValueVariant(statusValue) {
      if (statusValue === 'Completed' || statusValue === '100%') {
        return 'success';
      }
      if (parseInt(statusValue) >= 50) {
        return 'info';
      }
      return 'warning';
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    },

    formatDateTime(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.task-chat-container {
  max-width: 100%;
}

.task-details-card {
  background-color: #f8f9fa;
}

.chat-messages-card {
  height: 500px;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  max-height: 450px;
}

.message-item {
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border-radius: 8px;
}

.message-sent {
  background-color: #e3f2fd;
  margin-left: auto;
  max-width: 70%;
}

.message-received {
  background-color: #f5f5f5;
  margin-right: auto;
  max-width: 70%;
}

.task-info-message {
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 1rem;
}

.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.message-info {
  flex: 1;
}

.message-text {
  word-wrap: break-word;
  white-space: pre-wrap;
}

.status-update-badge {
  font-weight: 600;
}

.message-attachment,
.message-voice-note {
  margin-top: 0.5rem;
}

.message-input-card {
  border-top: 2px solid #e0e0e0;
}

.recording-indicator {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.attachment-preview,
.voice-note-preview {
  display: flex;
  align-items: center;
}

.cursor-pointer {
  cursor: pointer;
}

.task-detail-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.task-details-card {
  background: linear-gradient(to right, #f8f9fa 0%, #ffffff 100%);
  border-left: 3px solid #7367f0;
  padding: 0.75rem;
}

.task-header-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.task-title {
  font-size: 1rem;
  color: #495057;
  margin: 0;
}

.task-details-row {
  margin-bottom: 0.5rem;
}

.task-details-col {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.task-detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.task-description-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
}

.task-attachments-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
}

.attachments-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.attachment-item-inline {
  display: inline-flex;
  align-items: center;
}

.attachment-item-inline a {
  color: #7367f0;
  text-decoration: none;
}

.attachment-item-inline a:hover {
  text-decoration: underline;
}

.voice-player-inline {
  max-width: 250px;
  height: 28px;
  vertical-align: middle;
}

.attachment-container {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.attachment-info {
  flex: 1;
}

.attachment-link {
  color: #7367f0;
  text-decoration: none;
}

.attachment-link:hover {
  text-decoration: underline;
  color: #5a4fcf;
}

/* Scrollbar styling */
.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.task-attachment-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  margin-bottom: 10px;
  min-height: 80px;
}

.task-attachment-item .attachment-info {
  flex: 1;
}
</style>

